/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

/**
 * A single regular-file entry extracted from a tar archive.
 */
export interface TarEntry {
  name: string;
  data: Uint8Array;
}

/**
 * Parses a raw (already gzip-decompressed) tar byte array into named file
 * entries. Handles standard POSIX tar and the GNU long-filename ("L") extension.
 *
 * Directories and non-regular entries are skipped; only regular files are
 * returned. Callers are responsible for gzip decompression (e.g. pako.ungzip)
 * before calling this.
 */
export function parseTarEntries(buffer: Uint8Array): TarEntry[] {
  const entries: TarEntry[] = [];
  let offset = 0;
  const byteBlockSize = 512;
  let pendingLongName: string | null = null;

  const readString = (start: number, length: number): string => {
    let end = start;
    while (end < start + length && buffer[end] !== 0) end++;
    return new TextDecoder().decode(buffer.slice(start, end));
  };

  while (offset + byteBlockSize <= buffer.length) {
    // Two consecutive all-zero 512-byte blocks mark end-of-archive.
    let allZero = true;
    for (let blockMark = 0; blockMark < byteBlockSize; blockMark++) {
      if (buffer[offset + blockMark] !== 0) {
        allZero = false;
        break;
      }
    }
    if (allZero) break;

    const typeFlag = String.fromCharCode(buffer[offset + 156]);
    const rawName = readString(offset, 100);
    const prefix = readString(offset + 345, 155);
    const name = pendingLongName ?? (prefix ? `${prefix}/${rawName}` : rawName);
    pendingLongName = null;

    const sizeOctal = readString(offset + 124, 12).trim();
    const size = parseInt(sizeOctal, 8) || 0;
    const dataOffset = offset + byteBlockSize;

    if (typeFlag === "L") {
      // GNU long filename — the data block contains the real name.
      pendingLongName = new TextDecoder()
        .decode(buffer.slice(dataOffset, dataOffset + size))
        .replace(new RegExp(String.fromCharCode(0), "g"), "");
    } else if (typeFlag === "0" || typeFlag === "\0" || typeFlag === "") {
      // Regular file.
      entries.push({ name, data: buffer.slice(dataOffset, dataOffset + size) });
    }

    offset = dataOffset + Math.ceil(size / byteBlockSize) * byteBlockSize;
  }

  return entries;
}
